import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { addBooking } from '@redturtle/volto-io-prenoto/actions/booking';
import { PrenotazioneCardIndirizzoOrario } from '@redturtle/volto-io-prenoto/components/View/PrenotazioneView';
import { Icon } from 'design-comuni-plone-theme/components/ItaliaTheme';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  StepperNav,
} from 'design-react-kit';
import { getFormattedTime } from '@redturtle/volto-io-prenoto/helpers/date';

const messages = defineMessages({
  riepilogo_title: {
    id: 'booking_riepilogo_title',
    defaultMessage: 'Riepilogo',
  },
  edit_button: {
    id: 'booking_edit_button',
    defaultMessage: 'Modifica',
  },
  ufficio_title: {
    id: 'booking_ufficio_title',
    defaultMessage: 'Ufficio',
  },
  ufficio_tipo: {
    id: 'booking_ufficio_tipo',
    defaultMessage: 'Tipologia ufficio',
  },
  ufficio_municipalita: {
    id: 'booking_ufficio_municipalita',
    defaultMessage: 'Agenda',
  },
  data_title: {
    id: 'booking_data_title',
    defaultMessage: 'Data e orario',
  },
  data_label: {
    id: 'booking_data_label',
    defaultMessage: 'Data',
  },
  ora_label: {
    id: 'booking_ora_label',
    defaultMessage: 'Ora',
  },
  step_details: {
    id: 'booking_step_details',
    defaultMessage: 'Dettagli appuntamento',
  },
  motivo_label: {
    id: 'booking_motivo_label',
    defaultMessage: 'Motivo',
  },
  details_label: {
    id: 'booking_details_label',
    defaultMessage: 'Dettagli',
  },
  request_title: {
    id: 'booking_request_title',
    defaultMessage: 'Richiedente',
  },
  button_send: {
    id: 'booking_button_send',
    defaultMessage: 'Invia',
  },
  button_prev: {
    id: 'booking_button_prev',
    defaultMessage: 'Indietro',
  },
});

const PrenotazioneRiepilogo = ({
  prenotazioneObj,
  setActiveStep,
  setBackToSlots,
  scrollToTop,
  prenotazioneState,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const bookingSchemaResult = useSelector(
    (state) => state.bookingSchema?.result,
  );
  const siteTZ = useSelector(
    (state) => state?.site?.data['plone.portal_timezone'],
  );

  const bookableTypes = bookingSchemaResult?.booking_types?.bookable || [];

  const folder = prenotazioneObj.booking_folder;
  const date = prenotazioneObj.booking_date;
  const booking_type = prenotazioneObj.booking_type.value;
  const fields = prenotazioneObj.booking_fields.filter(
    (s) => s.name != 'description',
  );
  let booking_description_field = prenotazioneObj.booking_fields.filter(
    (s) => s.name === 'description',
  );
  // const currentBookingType = useMemo(() => {
  //   return bookableTypes.find(
  //     (el) => el.name === prenotazioneObj.booking_type?.value,
  //   );
  // }, [bookableTypes, prenotazioneObj.booking_type]);

  const additionalFieldSchema = useMemo(() => {
    const selectedBookyingType = bookableTypes?.find(
      (b) => b.name === booking_type,
    );
    return (
      selectedBookyingType?.booking_additional_fields_schema || []
    ).reduce((acc, field) => {
      acc[field.name] = field;
      return acc;
    }, {});
  }, [bookableTypes, booking_type]);

  const riepilogoSchema = [
    // Ufficio
    {
      titleSection: intl.formatMessage(messages.ufficio_title),
      info: [
        {
          label: intl.formatMessage(messages.ufficio_tipo),
          value: prenotazioneObj.booking_office.label,
        },
        {
          label: intl.formatMessage(messages.ufficio_municipalita),
          value: folder.title,
        },
      ],
    },
    // Data e ora
    {
      titleSection: intl.formatMessage(messages.data_title),
      info: [
        {
          label: intl.formatMessage(messages.data_label),
          value: getFormattedTime({
            date: date,
            tz: siteTZ,
            format: 'date_human',
          }),
        },
        {
          label: intl.formatMessage(messages.ora_label),
          value: getFormattedTime({
            date: date,
            tz: siteTZ,
            format: 'time',
          }),
        },
      ],
    },
    // Dettagli appuntamento
    {
      titleSection: intl.formatMessage(messages.step_details),
      info: [
        {
          label: intl.formatMessage(messages.motivo_label),
          value: booking_type,
        },
        ...(booking_description_field?.length === 1
          ? [
              {
                label: intl.formatMessage(messages.details_label),
                ...booking_description_field[0],
              },
            ]
          : []),
        ...(prenotazioneObj.additional_fields || []).map((field) => ({
          label: additionalFieldSchema?.[field.name]?.label || field.name,
          value: field.value,
        })),
      ],
    },
    // Form richiedente
    {
      titleSection: intl.formatMessage(messages.request_title),
      info: fields,
    },
  ];

  // TODO: change dispatch pattern
  const saveBooking = () => {
    // reset backToSlots
    setBackToSlots(false);
    dispatch(
      addBooking({
        folderPath: folder['@id'],
        bookingDate: date,
        bookingType: booking_type,
        fields: prenotazioneObj.booking_fields,
        additional_fields: prenotazioneObj.additional_fields,
        other_fields: {
          booking_address: prenotazioneObj.booking_folder?.address?.['@id'],
          booking_office: prenotazioneObj.booking_office?.['href'], // XXX: this is crappy, we should use the @id
          booking_contact_info: (
            prenotazioneObj.booking_office?.contact_info || []
          ).map((c) => c['@id']),
        },
      }),
    );
  };
  return (
    <>
      <Col lg="8" className="mx-auto mb-5 mt-3" id="prenotazioneRiepilogo">
        <Card className="section-form--card mb-0 no-after">
          <h2 className="mb-4">
            {intl.formatMessage(messages.riepilogo_title)}
          </h2>
          {/* Preview sections */}
          {riepilogoSchema &&
            riepilogoSchema.map((section, index) => (
              <Card className="no-after" key={'card_preview_' + index}>
                <CardHeader className="px-0 mx-4 d-flex justify-content-between align-items-center">
                  <h4 className="mb-0">{section.titleSection}</h4>
                  <Button
                    color="link"
                    size="lg"
                    className="steppers-btn-prev fw-semibold ms-0 px-0"
                    onClick={() => {
                      setActiveStep(index);
                      scrollToTop();
                    }}
                  >
                    {intl.formatMessage(messages.edit_button)}
                  </Button>
                </CardHeader>
                <CardBody className="radio-card--info pb-2">
                  {section.info.map(
                    (info, i) =>
                      info.value && (
                        <p key={'card_preview_' + index + '_' + i}>
                          {info.label}
                          <span className="d-block fw-semibold pb-2">
                            {info.value}
                          </span>
                        </p>
                      ),
                  )}
                  {index === 0 && (
                    <PrenotazioneCardIndirizzoOrario
                      folder={folder}
                      address={folder?.address}
                    />
                  )}
                </CardBody>
              </Card>
            ))}
        </Card>
      </Col>

      {/* ACTIONS BUTTONS */}
      <Col xs="12" lg="8" className="mx-auto">
        <StepperNav className="px-0 pt-3 mt-0">
          {/* PREV BUTTON */}
          <Button
            color="link"
            size="sm"
            className="steppers-btn-prev ms-0 ps-0 pe-2"
            onClick={() => {
              setActiveStep(3);
              scrollToTop();
            }}
          >
            <Icon
              icon="it-chevron-left"
              color="primary"
              size="sm"
              className="d-inline"
            />
            {intl.formatMessage(messages.button_prev)}
          </Button>

          {/* SAVE DRAFT BUTTON */}
          {/* <Button
            outline
            color="primary"
            size="sm"
            className="mx-auto steppers-btn-save"
          >
            {intl.formatMessage(messages.button_save_draft)}
          </Button> */}

          {/* SEND BUTTON */}
          <Button
            color="primary"
            size="sm"
            className="me-0 ms-5 steppers-btn-confirm"
            onClick={() => saveBooking()}
            disabled={prenotazioneState.loading}
          >
            {intl.formatMessage(messages.button_send)}
          </Button>
        </StepperNav>
      </Col>
    </>
  );
};

export default PrenotazioneRiepilogo;
